import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export type ThemeSetting = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export const STORAGE_KEY_THEME = "tb_admin_theme";

const THEME_COLORS: Record<ResolvedTheme, string> = {
  light: "#f9f7f3",
  dark: "#11100e",
};

function systemThemeMediaQuery(): MediaQueryList | undefined {
  try {
    return typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-color-scheme: dark)")
      : undefined;
  } catch {
    return undefined;
  }
}

export function detectSystemTheme(): ResolvedTheme {
  return systemThemeMediaQuery()?.matches ? "dark" : "light";
}

export function resolveTheme(setting: ThemeSetting): ResolvedTheme {
  return setting === "system" ? detectSystemTheme() : setting;
}

export function applyTheme(resolved: ResolvedTheme) {
  document.documentElement.classList.toggle("dark", resolved === "dark");
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", THEME_COLORS[resolved]);
}

type ThemeContextValue = {
  themeSetting: ThemeSetting;
  resolvedTheme: ResolvedTheme;
  setThemeSetting: (theme: ThemeSetting) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeSetting, setThemeSettingState] = useState<ThemeSetting>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_THEME);
      if (saved === "light" || saved === "dark" || saved === "system") return saved;
    } catch {
      // Fall through to default
    }
    return "system";
  });
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => detectSystemTheme());

  const resolvedTheme: ResolvedTheme = themeSetting === "system" ? systemTheme : themeSetting;

  useEffect(() => {
    if (themeSetting !== "system") return;
    const media = systemThemeMediaQuery();
    setSystemTheme(media?.matches ? "dark" : "light");
    if (!media || typeof media.addEventListener !== "function") return;
    const onChange = (event: MediaQueryListEvent) =>
      setSystemTheme(event.matches ? "dark" : "light");
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [themeSetting]);

  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  const setThemeSetting = (theme: ThemeSetting) => {
    setThemeSettingState(theme);
    try {
      localStorage.setItem(STORAGE_KEY_THEME, theme);
    } catch {
      // Ignore storage error
    }
  };

  const value = useMemo<ThemeContextValue>(
    () => ({ themeSetting, resolvedTheme, setThemeSetting }),
    [themeSetting, resolvedTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    return { themeSetting: "system", resolvedTheme: detectSystemTheme(), setThemeSetting: () => {} };
  }
  return context;
}
